/**
 * VDL Upload History Tab Component
 * 
 * Displays VDL upload history for a specific project
 */

import { useState, useEffect } from 'react'
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  message,
  Alert,
  Statistic,
  Row,
  Col,
} from 'antd'
import {
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloudUploadOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import api from '@/services/api'

const { Text } = Typography

interface VDLUploadHistoryTabProps {
  projectId: number
}

interface VDLUploadHistoryItem {
  id: number
  vdl_upload_task_id: number
  parsing_task_id: string
  source_id?: number
  project_id?: number
  source_value: string
  source_type: string
  subsource?: string
  source_from?: string
  upload_status: 'uploaded' | 'failed' | 'skipped' | 'duplicate'
  upload_result?: string
  vdl_id?: number
  vdl_response?: any
  parsing_metadata?: any
  created_at: string
  uploaded_at?: string
}

interface VDLUploadStats {
  total_uploads: number
  successful_uploads: number
  failed_uploads: number
  skipped_uploads: number
  duplicate_uploads: number
  upload_rate: number
}

const statusColors: Record<string, string> = {
  uploaded: 'success',
  failed: 'error',
  skipped: 'warning',
  duplicate: 'default',
}

const statusIcons: Record<string, any> = {
  uploaded: <CheckCircleOutlined />,
  failed: <CloseCircleOutlined />,
  skipped: <CloudUploadOutlined />,
  duplicate: <UploadOutlined />,
}

function VDLUploadHistoryTab({ projectId }: VDLUploadHistoryTabProps) {
  const [history, setHistory] = useState<VDLUploadHistoryItem[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  })
  const [stats, setStats] = useState<VDLUploadStats | null>(null)

  useEffect(() => {
    loadHistory()
    loadStats()
  }, [projectId, pagination.current, pagination.pageSize])

  const loadHistory = async () => {
    try {
      setLoading(true)
      
      const data = await api.getProjectVdlUploadHistory(projectId, {
        page: pagination.current,
        page_size: pagination.pageSize,
      })
      
      console.log('📊 VDL Upload history loaded:', data)
      
      setHistory(data.items || [])
      setPagination(prev => ({
        ...prev,
        total: data.total || 0,
      }))
      
      console.log(`✅ Loaded ${data.items?.length || 0} upload history items for project ${projectId}`)
      
    } catch (error: any) {
      console.error('❌ Error loading VDL upload history:', error)
      
      if (history.length === 0) {
        console.log('ℹ️ No upload history yet (first load)')
      } else {
        message.error('Ошибка загрузки истории загрузки')
      }
    } finally {
      setLoading(false)
    }
  }

  const loadStats = async () => {
    try {
      const data = await api.getVdlUploadStats({ project_id: projectId })
      setStats(data)
    } catch (error: any) {
      console.error('❌ Error loading stats:', error)
    }
  }

  const handleTableChange = (newPagination: any) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: pagination.total,
    })
  }

  const columns: ColumnsType<VDLUploadHistoryItem> = [
    {
      title: 'Источник',
      dataIndex: 'source_value',
      key: 'source_value',
      ellipsis: true,
      width: 300,
      render: (value: string, record: VDLUploadHistoryItem) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontSize: 13 }}>{value}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {record.source_type}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'upload_status',
      key: 'upload_status',
      width: 120,
      render: (status: string) => (
        <Tag icon={statusIcons[status]} color={statusColors[status]}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Результат',
      dataIndex: 'upload_result',
      key: 'upload_result',
      ellipsis: true,
      width: 200,
      render: (result: string) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {result || '-'}
        </Text>
      ),
    },
    {
      title: 'VDL ID',
      dataIndex: 'vdl_id',
      key: 'vdl_id',
      width: 100,
      render: (vdlId: number) => (
        vdlId ? (
          <Tag color="blue">{vdlId}</Tag>
        ) : (
          <Text type="secondary">-</Text>
        )
      ),
    },
    {
      title: 'Время загрузки',
      dataIndex: 'uploaded_at',
      key: 'uploaded_at',
      width: 160,
      render: (date: string) => (
        date ? dayjs(date).format('DD.MM.YYYY HH:mm:ss') : '-'
      ),
    },
  ]

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Statistics */}
        {stats && (
          <Card size="small" title="📊 Статистика загрузок">
            <Row gutter={16}>
              <Col span={4}>
                <Statistic
                  title="Всего"
                  value={stats.total_uploads}
                  valueStyle={{ fontSize: 24 }}
                />
              </Col>
              <Col span={5}>
                <Statistic
                  title="Успешно"
                  value={stats.successful_uploads}
                  valueStyle={{ color: '#52c41a', fontSize: 24 }}
                  prefix={<CheckCircleOutlined />}
                />
              </Col>
              <Col span={5}>
                <Statistic
                  title="Неудачно"
                  value={stats.failed_uploads}
                  valueStyle={{ color: '#ff4d4f', fontSize: 24 }}
                  prefix={<CloseCircleOutlined />}
                />
              </Col>
              <Col span={5}>
                <Statistic
                  title="Дубликаты"
                  value={stats.duplicate_uploads}
                  valueStyle={{ fontSize: 24 }}
                />
              </Col>
              <Col span={5}>
                <Statistic
                  title="Успешность"
                  value={stats.upload_rate}
                  suffix="%"
                  precision={1}
                  valueStyle={{ fontSize: 24 }}
                />
              </Col>
            </Row>
          </Card>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              loadHistory()
              loadStats()
            }}
            loading={loading}
          >
            Обновить
          </Button>
        </div>

        <Card>
          {history.length === 0 && !loading && (
            <Alert
              message="Нет истории загрузки для этого проекта"
              description={
                <div>
                  <p>История загрузок в VDL будет отображаться здесь после выполнения парсинга и загрузки.</p>
                  <p><strong>Как запустить:</strong></p>
                  <ol>
                    <li>Запустите парсинг Yandex Webmaster</li>
                    <li>После завершения парсинга загрузите результаты в VDL</li>
                    <li>История загрузок появится здесь</li>
                  </ol>
                </div>
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          
          <Table
            columns={columns}
            dataSource={history}
            loading={loading}
            rowKey="id"
            size="small"
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showTotal: (total) => `Всего: ${total} загрузок`,
              pageSizeOptions: ['20', '50', '100', '200'],
            }}
            onChange={handleTableChange}
          />
        </Card>
      </Space>
    </div>
  )
}

export default VDLUploadHistoryTab

